import React, { useEffect, useState } from "react";
import FundsService from "@services/Funds.service";
import Clipboard from "clipboard";
import Loader from "@appComponents/Loader/Loader";
import * as style from "./DepositQrCodeStyle.module.scss";
import Notice from "@appComponents/Notice/Notice";
import IDepositQrCode, {
  IDepositWallet,
  TDepositQrCode,
} from "./IDepositQrCode";
import { catchError } from "@features/utils/functions";
import { MAIN_CURRENCY } from "@bundles/consts";
import { Link } from "react-router-dom";
import { TReducers } from "@features/reducers";
import { connect } from "react-redux";
import web3 from "web3";

const DepositQrCode: React.FunctionComponent<IDepositQrCode> = (props) => {
  const [wallet, setWallet] = useState<TDepositQrCode>(undefined);

  useEffect(() => {
    const wallet = props.wallets ? findWallet(props.wallets) : undefined;

    if (!wallet) {
      FundsService.getDepositWallets(getWalletCurrency(), getNetworkType())
        .then((res) => {
          const wallet = res.data;
          props.updateState(wallet);
          setWallet(wallet);
        })
        .catch((err) => {
          window.scrollTo(0, 0);
          catchError(err);
        });
    } else {
      setWallet(wallet);
    }
  }, [props.selectedCoin]);

  useEffect(() => {
    const clipboard = new Clipboard(".click-to-copy");
    clipboard.on("success", (e) => {
      const text = e.trigger.innerHTML;
      e.trigger.innerHTML = "Address coppied!";
      setTimeout(() => {
        e.trigger.innerHTML = text;
      }, 1500);
      e.clearSelection();
    });
  }, []);

  const findWallet = (data: IDepositWallet[]) => {
    if (props.currency !== MAIN_CURRENCY) {
      return data.find((el: IDepositWallet) => el.currency === props.currency);
    }

    const walletCurrency = getSelectedBfusdWalletCurrency();
    return data.find(
      (el: IDepositWallet) =>
        el.currency === walletCurrency && el.network_type === getNetworkType()
    );
  };

  const getSelectedBfusdWalletCurrency = () =>
    props.selectedCoin === "usdt-ethereum"
      ? "usdt"
      : (props.selectedCoin as string);

  const getWalletCurrency = () =>
    props.currency !== MAIN_CURRENCY
      ? props.currency
      : getSelectedBfusdWalletCurrency();

  const getNetworkType = () => {
    switch (props.selectedCoin) {
      case "usdt-ethereum":
        return "ethereum";
      default:
        return undefined;
    }
  };

  const renderQR = (qrString: string) => {
    if (!qrString) {
      return;
    }

    return (
      <div
        id="qrCode"
        dangerouslySetInnerHTML={{
          __html: qrString,
        }}
      />
    );
  };

  const currency = getWalletCurrency().toUpperCase();

  const formatWalletAddress = (address: string) =>
    web3.utils.isAddress(address)
      ? web3.utils.toChecksumAddress(address)
      : address;

  return (
    <div>
      {currency !== "USDT" && currency !== "ETH" && currency !== "BTC" && (
        <Notice noDivider={true}>
          Only send {currency} on the supported blockchain to this deposit
          address. Depositing any other coin, token, Layer 2 asset, or USDT on
          an unsupported blockchain may result in a permanent loss of funds.{" "}
          {currency === "BFT" && props.slug ? (
            <>
              To learn more about BF Premium & Premium+ membership click{" "}
              <Link to={`/profile/${props.slug}/membership`}>here</Link>.
            </>
          ) : null}
        </Notice>
      )}
      {currency === "USDT" && (
        <Notice noDivider={true}>
          Send only USDT (ERC-20) on the Ethereum blockchain to this deposit
          address. Depositing any other asset, including coins, tokens, Layer 2
          assets, or USDT sent on unsupported networks may result in the
          permanent loss of your funds.
        </Notice>
      )}

      {currency === "BTC" && (
        <Notice noDivider={true}>
          Only send BTC on the supported blockchain to this deposit address.
          Depositing any other coin, token, Layer 2 asset, or BTC on an
          unsupported blockchain may result in a permanent loss of funds.
        </Notice>
      )}
      {currency === "ETH" && (
        <Notice noDivider={true}>
          Send only ETH on the Ethereum blockchain to this deposit address.
          Depositing any other asset, including tokens, coins, Layer 2 assets,
          or ETH sent on unsupported networks may result in the permanent loss
          of your funds.
        </Notice>
      )}

      {wallet ? (
        <>
          <div className={style.addressHolder}>
            <p>{currency} Deposit Address</p>
            <div className={style.address}>
              <p id="addressToCopy">{formatWalletAddress(wallet.address)}</p>
              <span
                className="click-to-copy"
                data-clipboard-target="#addressToCopy"
              >
                Copy Address
              </span>
            </div>
          </div>
          {renderQR(wallet.encoded_address_qr_code)}
        </>
      ) : (
        <Loader moreSpace={true} />
      )}
      <Notice noDivider={true}>
        Coins will be deposited immediately after 2 network confirmations. After
        making a deposit you can track its progress in the history.
      </Notice>
    </div>
  );
};

const mapStateToProps = (store: TReducers) => ({
  slug: store.userInfo.nickname,
});

export default React.memo(connect(mapStateToProps)(DepositQrCode));
